'use client';

import {useCountry} from '@/hooks';
import {useCatalogOffers} from '@/hooks/catalog';
import {useLocale} from '@epic-mw/i18n';
import {CatalogOfferCard} from './CatalogOfferCard.organism';
import {AnalyticsProps} from '@/types';
import {Stack} from '@eds-web/react';
import {Grid, GridItem} from '@panda/jsx';
import {
	CatalogOfferCardsFieldsFragment,
	CatalogOfferCardVariant,
	ConfigCatalogOfferFieldsFragment
} from '@common/hygraph-schema';
import {CatalogOffer} from 'epic-graphql-types';

const isValidCatalogId = (v: unknown): v is string => {
	return typeof v === 'string' && /^[a-z0-9]+$/i.test(v.trim());
};

const safeMapOfferIds = (offers: ConfigCatalogOfferFieldsFragment[]): string[] => {
	return offers
		?.map((o) => (typeof o.offerId === 'string' ? o.offerId.trim() : ''))
		.filter((id): id is string => isValidCatalogId(id));
};

const safeMapSandboxId = (sandboxId: string) => {
	return isValidCatalogId(sandboxId) ? sandboxId.trim() : '';
};

export const CatalogOfferCards = ({
	variant,
	checkout,
	sandboxId,
	offers,
	analytics
}: CatalogOfferCardsFieldsFragment & AnalyticsProps) => {
	const locale = useLocale();
	const countryCode = useCountry();

	const catalogOffers = useCatalogOffers(
		[
			{
				sandboxId: safeMapSandboxId(sandboxId),
				offerIds: safeMapOfferIds(offers)
			}
		],
		locale,
		countryCode
	);

	const isLoading = catalogOffers.isLoading;
	let data = catalogOffers.data || [];

	//tbd: we can come up with something better here to act as skeleton
	if (!data?.length) {
		switch (variant) {
			case CatalogOfferCardVariant.Subscribe: {
				data = [{}];
				break;
			}
			case CatalogOfferCardVariant.Inline:
			case CatalogOfferCardVariant.Outline: {
				data = [{}, {}, {}, {}];
				break;
			}
		}
	}

	const offersMap = offers?.reduce(
		(prev, offer) => {
			if (offer.overrideOfferFields) {
				const k = offer.offerId;
				if (!prev[k]) {
					prev[k] = {
						title: offer.offerTitle,
						description: offer.offerDesc
					};
				}
			}
			return prev;
		},
		{} as Record<string, CatalogOffer>
	);

	const getCatalogOverrideFields = (id: string) => {
		return offersMap[id] ?? {};
	};

	if (data.length === 1) {
		return data.map((card, i) => (
			<CatalogOfferCard
				key={i}
				{...card}
				{...getCatalogOverrideFields(card.id || '')}
				locale={locale}
				isLoading={isLoading}
				checkout={!!checkout}
				variant={variant}
				analytics={analytics}
			/>
		));
	}

	return (
		<Stack gap={16}>
			<Grid gap={4} gridTemplateColumns={{base: '1fr 1fr 1fr 1fr', mdDown: '1fr 1fr'}}>
				{data.map((card, i) => (
					<GridItem key={i} h="full">
						<CatalogOfferCard
							{...card}
							{...getCatalogOverrideFields(card.id || '')}
							locale={locale}
							isLoading={isLoading}
							checkout={!!checkout}
							variant={variant}
							analytics={analytics}
						/>
					</GridItem>
				))}
			</Grid>
		</Stack>
	);
};
